import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type DiscoveryComment = {
  id: string;
  discovery_id: string;
  user_id: string | null;
  guest_name: string | null;
  body: string;
  created_at: string;
  updated_at: string;
  author: {
    username: string | null;
    display_name: string | null;
    avatar_seed: string | null;
    avatar_style: string | null;
    archetype: string | null;
  } | null;
};

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

export const listDiscoveryComments = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ discoveryId: z.string().uuid(), limit: z.number().int().min(1).max(100).default(50) }).parse(d))
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: rows } = await (sb as any)
      .from("discovery_comments")
      .select("id, discovery_id, user_id, guest_name, body, created_at, updated_at")
      .eq("discovery_id", data.discoveryId)
      .eq("is_hidden", false)
      .order("created_at", { ascending: false })
      .limit(data.limit);
    const userIds = Array.from(new Set((rows ?? []).map((r: any) => r.user_id).filter(Boolean)));
    let authors = new Map<string, DiscoveryComment["author"]>();
    if (userIds.length > 0) {
      const { data: profs } = await (sb as any)
        .from("profiles")
        .select("id, username, display_name, avatar_seed, avatar_style, archetype, is_public")
        .in("id", userIds);
      (profs ?? []).forEach((p: any) => {
        authors.set(p.id, p.is_public ? {
          username: p.username, display_name: p.display_name,
          avatar_seed: p.avatar_seed, avatar_style: p.avatar_style, archetype: p.archetype,
        } : null);
      });
    }
    const items: DiscoveryComment[] = (rows ?? []).map((r: any) => ({
      id: r.id, discovery_id: r.discovery_id, user_id: r.user_id,
      guest_name: r.guest_name ?? null,
      body: r.body, created_at: r.created_at, updated_at: r.updated_at,
      author: r.user_id ? (authors.get(r.user_id) ?? null) : null,
    }));
    return { items };
  });

export const postDiscoveryComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({
    discoveryId: z.string().uuid(),
    body: z.string().trim().min(2).max(2000),
  }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await (supabase as any)
      .from("discovery_comments")
      .insert({ discovery_id: data.discoveryId, user_id: userId, body: data.body })
      .select("id, discovery_id, user_id, body, created_at, updated_at")
      .single();
    if (error) return { ok: false, error: error.message } as const;
    return { ok: true, comment: row as Omit<DiscoveryComment, "author" | "guest_name"> } as const;
  });

// Guest commenting: no account needed. Traffic arrives from Instagram with zero
// intent to sign up, so requiring an account meant the discussion stayed empty.
export const postGuestComment = createServerFn({ method: "POST" })
  .inputValidator((d) => z.object({
    discoveryId: z.string().uuid(),
    name: z.string().trim().min(2).max(32),
    body: z.string().trim().min(2).max(2000),
  }).parse(d))
  .handler(async ({ data }) => {
    const sb = publicClient();
    const name = data.name.replace(/\s+/g, " ").slice(0, 32);
    if (/https?:\/\/|www\.|\.com\b/i.test(data.body) && data.body.length < 40) {
      return { ok: false, error: "Links only? Add a thought too." } as const;
    }
    const { error } = await (sb as any)
      .from("discovery_comments")
      .insert({ discovery_id: data.discoveryId, user_id: null, guest_name: name, body: data.body, is_hidden: false });
    if (error) return { ok: false, error: error.message } as const;
    return { ok: true } as const;
  });


export const deleteMyComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ commentId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await (context.supabase as any)
      .from("discovery_comments")
      .delete()
      .eq("id", data.commentId)
      .eq("user_id", context.userId);
    if (error) return { ok: false, error: error.message } as const;
    return { ok: true } as const;
  });
